// Collections that fill themselves, and the binder's filter (SuiteModel/SavedSearch.swift): rules as facets (any of /
// all of their `values`), all/any, the manuscript or all text, members evaluated in reading order
// (`DocumentStore.members(of:in:)`) over `FilterFacts` (filter.ts) and never stored, shown as a read-only flat arrangement
// (role `collection`, node id = fragment id: the app's `resultsArrangement`). Stored as JSON per collection in
// `settings.collections`, byte for byte as the Swift writes it. Compile (Web/compile/src/source.ts) evaluates
// collections with this too. `design-docs/Binder filters — design.md`.
import type * as A from '@automerge/automerge';
import type { MetadataValue } from '../contract.ts';
import { sequence } from '../automerge/fractionalIndex.ts';
import { fragmentDocName, makeID } from '../edit/editProject.ts';
import { suiteJSON } from '../edit/storeEditor.ts';
import { effectiveLocks } from '../locks.ts';
import { orderedFragmentIDs, tree as buildTree, type ArrangementTreeNode } from '../model/arrangement.ts';
import { readFragment, type Arrangement, type ArrangementNode, type FragmentRecord, type ProjectRecord } from '../model/documents.ts';
import { counted, type SelectionEntry } from '../model/manuscriptSelection.ts';
import { standardCompare, swiftCompare } from '../model/order.ts';
import { trimCFWhitespace } from '../unicode.ts';
import { explicitPresence, presence, presenceRank, PRESENCE_LEVELS, reference, type PresenceLevel } from '../presence.ts';
import { filterContext, ownFacts, ruleMatches, type FilterContext, type FilterFacts } from './filter.ts';

/** `SavedSearch.Scope`; `unsupported` is a scope this build doesn't know (kept in `unsupportedScope`, matching nothing). */
export type SearchScope = 'manuscript' | 'all-text' | 'research' | 'unsupported';
/** `SavedSearch.Icon`: the icons a collection can wear (unknown or none: `stack`). */
export type CollectionIcon = 'stack' | 'envelope' | 'fog' | 'person' | 'eye' | 'key' | 'flag';
export const COLLECTION_ICONS: readonly CollectionIcon[] = ['stack', 'envelope', 'fog', 'person', 'eye', 'key', 'flag'];
/** `SavedSearch.Icon.choices`: the icons offered, after the default. */
export const COLLECTION_ICON_CHOICES: readonly CollectionIcon[] = ['envelope', 'fog', 'person', 'eye', 'key', 'flag'];
/** `SavedSearch.inks`: the colours offered for a collection, in order (`InkColor` raw values). */
export const COLLECTION_INKS = ['purple', 'blue', 'green', 'yellow', 'orange', 'red', 'pink'] as const;
const INK_NAMES = new Set(['yellow', 'green', 'blue', 'pink', 'purple', 'orange', 'red', 'grey']);
export type RuleKind = 'field' | 'text' | 'title' | 'synopsis' | 'notes' | 'entity' | 'modified-within' | 'included-in-compile' | 'words'
  | 'target' | 'locked' | 'kind' | 'within' | 'comments' | 'suggestions' | 'unsupported';
/** The kinds this build reads (`unsupported` is what any other reads as). */
export const RULE_KINDS: readonly RuleKind[] = ['field', 'text', 'title', 'synopsis', 'notes', 'entity', 'modified-within',
  'included-in-compile', 'words', 'target', 'locked', 'kind', 'within', 'comments', 'suggestions'];
/** `SavedSearch.Comparison`: how a rule compares a number (words) or tests text. */
export type Comparison = 'lt' | 'gt' | 'between' | 'empty' | 'contains';
export const COMPARISONS: readonly Comparison[] = ['lt', 'gt', 'between', 'empty', 'contains'];

/** `SavedSearch.Rule`: one facet. */
export interface SavedSearchRule {
  id: string;
  kind: RuleKind;
  /** `field`: the metadata field's id. */
  field?: string;
  /** Any of these (`all`: every one): option names or ids, a character's id, a tag ("" = empty), node ids, kinds.
   *  `text`/`title`/…: the words, first only (`ruleValue`). */
  values: string[];
  all: boolean;
  /** `entity`: the least presence that counts (absent: mentioned or more). */
  level?: PresenceLevel;
  op?: Comparison;
  number?: number;
  upper?: number;
  days?: number;
  /** `comments`: a category id, or "none". */
  category?: string;
  /** `comments`: "human" or "agent". */
  author?: string;
  /** "is not", "doesn't contain", "doesn't appear", "not modified within", "not included". */
  negate: boolean;
  /** The kind as stored, when this build doesn't know it (`kind` is then `unsupported`). */
  unsupportedKind?: string;
}

export interface SavedSearch {
  id: string;
  name: string;
  scope: SearchScope;
  /** All rules must match (else any one). A search without rules matches everything in scope. */
  matchAll: boolean;
  rules: SavedSearchRule[];
  /** An `InkColor` raw value (an unknown one is kept, and drawn as the default). */
  color?: string;
  /** A `CollectionIcon` (an unknown one is kept, drawn as `stack`). */
  icon?: string;
  /** The scope as stored, when this build doesn't know it (`scope` is then `unsupported`). */
  unsupportedScope?: string;
}

/** `SettingsKey.collections`. */
export const COLLECTIONS_KEY = 'collections';

export const newSavedSearch = (o: Partial<SavedSearch> & { name: string }): SavedSearch =>
  ({
    id: o.id ?? makeID(), name: o.name, scope: o.scope ?? 'manuscript', matchAll: o.matchAll ?? true, rules: o.rules ?? [],
    ...(o.color !== undefined ? { color: o.color } : {}), ...(o.icon !== undefined ? { icon: o.icon } : {}),
  });

/** `SavedSearch.ink`: the colour to draw — the stored one, else one picked by the id (the same as the Swift's). */
export function collectionInk(s: Pick<SavedSearch, 'id' | 'color'>): string {
  if (s.color !== undefined && INK_NAMES.has(s.color)) return s.color;
  let sum = 0;
  for (const c of s.id) sum += c.codePointAt(0)!;
  return COLLECTION_INKS[sum % COLLECTION_INKS.length];
}

/** `SavedSearch.shownIcon`. */
export const collectionIcon = (s: Pick<SavedSearch, 'icon'>): CollectionIcon =>
  (COLLECTION_ICONS as readonly string[]).includes(s.icon ?? '') ? s.icon as CollectionIcon : 'stack';

/** `Rule.init`: `value` is one value (`values` wins when both are given). */
export function newRule(o: Partial<SavedSearchRule> & { kind: RuleKind; value?: string }): SavedSearchRule {
  const rule: SavedSearchRule = {
    id: o.id ?? makeID(), kind: o.kind, values: o.values ?? (o.value !== undefined ? [o.value] : []), all: o.all ?? false, negate: o.negate ?? false,
  };
  for (const key of ['field', 'level', 'op', 'number', 'upper', 'days', 'category', 'author', 'unsupportedKind'] as const) {
    if (o[key] !== undefined) (rule as unknown as Record<string, unknown>)[key] = o[key];
  }
  return rule;
}

/** `Rule.value`: the first value (the words of a text rule, the entity of an older rule). */
export const ruleValue = (rule: Pick<SavedSearchRule, 'values'>): string | undefined => rule.values[0];

// MARK: JSON

const absent = (v: unknown) => v === undefined || v === null;
/** `decodeIfPresent`: undefined when absent or null, the value when it's of the type, else `bad` (the decode throws). */
const BAD = Symbol('bad');
function optional<T>(v: unknown, is: (v: unknown) => v is T): T | undefined | typeof BAD {
  if (absent(v)) return undefined;
  return is(v) ? v : BAD;
}
const isString = (v: unknown): v is string => typeof v === 'string';
const isBool = (v: unknown): v is boolean => typeof v === 'boolean';
const isNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isInt = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v);
const isStrings = (v: unknown): v is string[] => Array.isArray(v) && v.every(isString);
const isLevel = (v: unknown): v is PresenceLevel => typeof v === 'string' && (PRESENCE_LEVELS as readonly string[]).includes(v);
const isComparison = (v: unknown): v is Comparison => typeof v === 'string' && (COMPARISONS as readonly string[]).includes(v);

/** `Rule.init(from:)`: null when it can't be read (which makes its search unreadable). A kind this build doesn't know is
 *  kept as `unsupported` with its name; one value may be stored as `value` alone (as builds before `values` wrote it);
 *  an unknown level or comparison reads as none (`try?`). */
export function decodeRule(raw: unknown): SavedSearchRule | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const x = raw as Record<string, unknown>;
  if (typeof x.id !== 'string' || typeof x.kind !== 'string') return null;
  const field = optional(x.field, isString);
  let values = optional(x.values, isStrings);
  if (values === undefined) {
    const value = optional(x.value, isString);
    if (value === BAD) return null;
    values = value === undefined ? [] : [value];
  }
  const all = optional(x.all, isBool), number = optional(x.number, isNumber), upper = optional(x.upper, isNumber);
  const days = optional(x.days, isInt), category = optional(x.category, isString), author = optional(x.author, isString);
  const negate = optional(x.negate, isBool);
  if ([field, values, all, number, upper, days, category, author, negate].includes(BAD)) return null;
  const known = (RULE_KINDS as readonly string[]).includes(x.kind);
  const level = isLevel(x.level) ? x.level : undefined;
  const op = isComparison(x.op) ? x.op : undefined;
  return newRule({
    id: x.id, kind: known ? (x.kind as RuleKind) : 'unsupported', values: values as string[], all: (all as boolean | undefined) ?? false,
    negate: (negate as boolean | undefined) ?? false, field: field as string | undefined, level, op, number: number as number | undefined,
    upper: upper as number | undefined, days: days as number | undefined, category: category as string | undefined,
    author: author as string | undefined, ...(known ? {} : { unsupportedKind: x.kind }),
  });
}

/** `Rule.encode(to:)`, as a plain object for `suiteJSON`: one value is written as `value` alone, so a build from before
 *  `values` reads it as it always has; more are written as `values`, with the first as `value` too. `all` only when
 *  set; a kind this build doesn't know as it was stored. */
export const encodeRule = (r: SavedSearchRule) => ({
  id: r.id, kind: r.kind === 'unsupported' ? (r.unsupportedKind ?? r.kind) : r.kind, field: r.field, value: r.values[0],
  values: r.values.length > 1 ? r.values : undefined, all: r.all ? true : undefined, level: r.level, op: r.op, number: r.number,
  upper: r.upper, days: r.days, category: r.category, author: r.author, negate: r.negate,
});

/** One stored collection, as `Codable` reads it: a rule that can't be read makes the search unreadable (it's left out,
 *  and never written over); a rule of a kind this build doesn't know keeps it, matching nothing (`isUnsupported`). Keys
 *  this build doesn't know are ignored here and kept when saving. */
export function decodeSavedSearch(raw: unknown): SavedSearch | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.id !== 'string' || typeof o.name !== 'string' || !Array.isArray(o.rules)) return null;
  if (o.scope !== undefined && typeof o.scope !== 'string') return null;
  if (typeof o.color !== 'undefined' && typeof o.color !== 'string') return null;
  if (typeof o.icon !== 'undefined' && typeof o.icon !== 'string') return null;
  const rules: SavedSearchRule[] = [];
  for (const r of o.rules as unknown[]) {
    const rule = decodeRule(r);
    if (!rule) return null;
    rules.push(rule);
  }
  const scope = o.scope ?? 'manuscript';
  const known = scope === 'manuscript' || scope === 'all-text' || scope === 'research';
  const search: SavedSearch = { id: o.id, name: o.name, scope: known ? scope as SearchScope : 'unsupported', matchAll: o.matchAll !== false, rules };
  if (!known) search.unsupportedScope = scope as string;
  if (o.color !== undefined) search.color = o.color as string;
  if (o.icon !== undefined) search.icon = o.icon as string;
  return search;
}

/** What `JSONEncoder` writes of a search (nil optionals left out). */
export const encodeSavedSearch = (s: SavedSearch) => ({
  id: s.id, name: s.name, scope: s.scope === 'unsupported' ? s.unsupportedScope ?? s.scope : s.scope, matchAll: s.matchAll,
  rules: s.rules.map(encodeRule), ...(s.color !== undefined ? { color: s.color } : {}), ...(s.icon !== undefined ? { icon: s.icon } : {}),
});

/** `SavedSearch.isUnsupported`: made by a newer build with rules or a scope this one can't read — it matches nothing
 *  here and isn't edited. */
export const isUnsupported = (s: Pick<SavedSearch, 'rules'> & { scope?: SearchScope }) =>
  s.scope === 'unsupported' || s.rules.some((r) => r.kind === 'unsupported');

/** `Rule.meaning ==`: the same rule but for its id (every stored property compared, as Swift's `==`). */
export function sameRule(a: SavedSearchRule, b: SavedSearchRule): boolean {
  return suiteJSON({ ...encodeRule(a), id: '', kind: a.kind, unsupportedKind: a.unsupportedKind })
    === suiteJSON({ ...encodeRule(b), id: '', kind: b.kind, unsupportedKind: b.unsupportedKind });
}

/** `SavedSearch.sameFilter(as:)`: whether another filter says the same thing (scope, all/any and rules, ignoring the
 *  rules' ids and the name). */
export function sameFilter(a: SavedSearch, b: SavedSearch): boolean {
  return a.scope === b.scope && (a.matchAll === b.matchAll || a.rules.length < 2)
    && a.rules.length === b.rules.length && a.rules.every((r, i) => sameRule(r, b.rules[i]));
}

/** `ProjectDocument.savedSearches()`: every readable search, by name (`localizedStandardCompare`). */
export function savedSearchesOf(settings: Record<string, unknown>): SavedSearch[] {
  const all = settings[COLLECTIONS_KEY];
  if (!all || typeof all !== 'object') return [];
  const out: SavedSearch[] = [];
  for (const raw of Object.values(all as Record<string, unknown>)) {
    if (typeof raw !== 'string') continue;
    try {
      const s = decodeSavedSearch(JSON.parse(raw));
      if (s) out.push(s);
    } catch { /* unreadable: left out, as `try?` */ }
  }
  return out.sort((a, b) => standardCompare(a.name, b.name));
}

/** `SavedSearch.isNamed`: by id, or by name ignoring case and surrounding spaces (compile's `collection:`). */
export function isNamed(search: SavedSearch, reference: string): boolean {
  return search.id === reference || trimCFWhitespace(search.name).toLowerCase() === trimCFWhitespace(reference).toLowerCase();
}

// MARK: Matching

/** `SavedSearch.matches`: nothing for a search made by a newer build, everything for one without rules. */
export function searchMatches(search: SavedSearch, facts: FilterFacts, context: FilterContext = filterContext()): boolean {
  if (isUnsupported(search)) return false;
  if (!search.rules.length) return true;
  return search.matchAll ? search.rules.every((r) => ruleMatches(r, facts, context)) : search.rules.some((r) => ruleMatches(r, facts, context));
}

// MARK: Members

/** `MetadataValue.displayString` (en-US, as the fixtures are made). */
export function displayString(v: MetadataValue): string {
  switch (v.type) {
    case 'text': return v.value;
    case 'number': return Number.isInteger(v.value) ? v.value.toLocaleString('en-US') : v.value.toLocaleString('en-US', { maximumFractionDigits: 6 });
    case 'boolean': return v.value ? 'Yes' : 'No';
    case 'date': return v.value.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  }
}

const selectionEntries = (project: ProjectRecord) =>
  new Map<string, SelectionEntry>([...project.fragments].map(([id, e]) => [id, { kind: e.kind, trashed: e.trashed }]));

/** `ProjectDocument.readingOrder(entries:)`: every fragment outside the trash in reading order — the manuscript, then the
 *  other arrangements in theirs, then anything not placed in one (by id). */
export function readingOrderAll(project: ProjectRecord): string[] {
  const entries = selectionEntries(project);
  const seen = new Set<string>();
  const order: string[] = [];
  const arrangements = project.arrangements.filter((a) => a.role !== 'trash')
    .sort((a, b) => (a.role === 'manuscript' ? 0 : 1) - (b.role === 'manuscript' ? 0 : 1) || a.order - b.order);
  for (const a of arrangements) {
    for (const id of orderedFragmentIDs(a)) {
      const e = entries.get(id);
      if (e && !e.trashed && !seen.has(id)) { seen.add(id); order.push(id); }
    }
  }
  return [...order, ...[...entries].filter(([id, e]) => !e.trashed && !seen.has(id)).map(([id]) => id).sort(swiftCompare)];
}

/** `DocumentStore.members(of:in:)`: each search's fragments in reading order, by search id. Exact: every document is read
 *  (at a checkpoint's state too, for compile), where the binder's filter uses `liveFacts`. */
export function searchMembers(docs: Map<string, A.Doc<unknown>>, project: ProjectRecord, searches: SavedSearch[], now: Date = new Date()): Map<string, string[]> {
  const result = new Map<string, string[]>(searches.map((s) => [s.id, []]));
  if (!searches.length) return result;
  const entries = selectionEntries(project);
  const manuscriptArrangement = project.arrangements.find((a) => a.role === 'manuscript') ?? null;
  const inManuscript = counted(manuscriptArrangement, entries, false);
  const manuscript = new Set(inManuscript);
  const order = searches.some((s) => s.scope !== 'manuscript') ? readingOrderAll(project) : inManuscript;
  const research = new Set(searches.some((s) => s.scope === 'research')
    ? project.arrangements.filter((a) => a.role === 'reference').flatMap((a) => orderedFragmentIDs(a)) : []);
  const tagFields = project.fields.filter((x) => x.type === 'tags').map((x) => x.id);
  const rules = searches.flatMap((s) => s.rules);
  const needsPresence = rules.some((r) => r.kind === 'entity');
  const needsText = rules.some((r) => r.kind === 'text' || r.kind === 'notes');
  const needsLocks = rules.some((r) => r.kind === 'locked');
  const records = new Map<string, FragmentRecord | null>();
  const record = (id: string) => {
    if (!records.has(id)) {
      const doc = docs.get(fragmentDocName(id));
      records.set(id, doc ? readFragment(doc) : null);
    }
    return records.get(id)!;
  };
  const locks = needsLocks
    ? effectiveLocks(project.arrangements, new Set([...project.fragments.keys()].filter((id) => record(id)?.locked)))
    : new Set<string>();
  const arrangements = project.arrangements.filter((a) => a.role !== 'trash')
    .sort((a, b) => (a.role === 'manuscript' ? 0 : 1) - (b.role === 'manuscript' ? 0 : 1) || a.order - b.order);

  // First every document's own facts, then where they sit (nodes and the words under them).
  const facts = new Map<string, FilterFacts>();
  for (const id of order) {
    const f = record(id);
    if (!f) continue;
    const levels = new Map<string, PresenceLevel>();
    if (needsPresence) {
      for (const p of presence(f.text, reference(f.metadata, 'pov'), explicitPresence(f.presence), project.entities, f.kind)) {
        if (p.level === 'absent') continue;
        const was = levels.get(p.entityID);
        if (was === undefined || presenceRank(p.level) > presenceRank(was)) levels.set(p.entityID, p.level);
      }
    }
    facts.set(id, { ...ownFacts(f, { tagFields, presence: levels, locked: locks.has(id), text: needsText }), id });
  }
  const placed = new Set<string>();
  for (const a of arrangements) {
    const walk = (nodes: ArrangementTreeNode[], above: Set<string>): number => {
      let sum = 0;
      for (const n of nodes) {
        const path = new Set(above).add(n.node.id);
        const below = walk(n.children, path);
        const f = n.node.fragmentID;
        const ff = f !== undefined ? facts.get(f) : undefined;
        if (f === undefined || !ff) { sum += below; continue; }
        sum += ff.words + below;
        for (const id of path) ff.nodes.add(id);
        // Word targets count what's under the document where it's first placed (the manuscript, if there).
        if (!placed.has(f)) {
          placed.add(f);
          ff.subtreeWords = ff.words + below;
          ff.isFolder = n.node.isFolder || n.children.length > 0;
        }
      }
      return sum;
    };
    walk(buildTree(a), new Set());
  }

  const context = filterContext({ fields: project.fields, now });
  for (const id of order) {
    const f = facts.get(id);
    if (!f) continue;
    for (const s of searches) {
      const inScope = s.scope === 'all-text' || (s.scope === 'research' ? research : manuscript).has(id);
      if (inScope && searchMatches(s, f, context)) result.get(s.id)!.push(id);
    }
  }
  return result;
}

/** `resultsArrangement`: members as a flat, read-only arrangement (role `collection`), one node per fragment whose id is
 *  the fragment's, in reading order. */
export function resultsArrangement(search: Pick<SavedSearch, 'id' | 'name'>, members: string[]): Arrangement {
  const positions = sequence(members.length);
  const nodes = new Map<string, ArrangementNode>();
  members.forEach((id, i) => { if (!nodes.has(id)) nodes.set(id, { id, fragmentID: id, isFolder: false, position: positions[i] }); });
  return { id: search.id, name: search.name, role: 'collection', order: 0, nodes };
}

/** Every collection's results, as the app caches them (`ProjectSnapshot.searchResults`). */
export function collectionArrangements(docs: Map<string, A.Doc<unknown>>, project: ProjectRecord, now: Date = new Date()): Map<string, Arrangement> {
  const searches = savedSearchesOf(project.settings);
  const members = searchMembers(docs, project, searches, now);
  return new Map(searches.map((s) => [s.id, resultsArrangement(s, members.get(s.id) ?? [])]));
}
